import type Guid from "_shared/guids/guid";
import { type ReactNode, useState } from "react";
import { useSession } from "users/__session/session-context";
import type Session from "users/__session/session";
import { useSaveProposalTemplateMutation } from "work/proposal-templates/_api/proposal-template-api";
import {
  DEFAULT_TEAM_CHOICES,
  type ProposalTemplateTeamChoices,
  type SaveProposalTemplatePayload,
} from "work/proposal-templates/_api/save-proposal-template-api-request";
import ProposalTemplateTeamChoicesFields from "work/proposal-templates/_views/proposal-template-team-choices";
import { proposalTemplatePartyOf } from "work/proposal-templates/proposal-template";
import {
  SAVE_AS_PROPOSAL_TEMPLATE_LABEL,
  SAVE_AS_PROPOSAL_TEMPLATE_TOOLTIP,
  saveProposalAsTemplate,
} from "work/proposal-templates/save-proposal-as-template";
import type { ProposalSecondaryAction } from "work/proposals/_views/_buttons/proposal-secondary-actions";
import useCreateTemplateAction from "work/proposals/_views/_shared/use-create-template-action";
import { useLazyGetProposalTeamReuseQuery } from "work/proposals/drafts-from-previous/_api/proposal-team-reuse-api";
import ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import type Proposal from "work/proposals/proposal";

export type SaveAsProposalTemplateAction = {
  /**
   * The page action, or none where the proposal cannot be saved as a template. The page adds its own
   * reasons to disable it, such as a save in progress, where it builds its action list.
   */
  actions: ProposalSecondaryAction[];
  /** The dialog that names the template. Mount it once on the page. */
  dialog: ReactNode;
  /** Opens the naming dialog, for a page that draws its own button rather than a page action. */
  open: () => void;
  isSaving: boolean;
};

/**
 * "Save as Proposal Template" on a proposal's own page (#6129): saves the reader's side of the saved
 * proposal as a named template that new drafts can start from.
 *
 * Offered on the same terms as Create from Previous: to someone who may create proposals, while there
 * is a saved proposal they can still read. A kept view of a proposal they no longer can is not offered.
 */
export default function useSaveAsProposalTemplateAction(args: {
  proposal: Proposal | null | undefined;
  canCreateProposals: boolean;
}): SaveAsProposalTemplateAction {
  const { proposal, canCreateProposals } = args;
  const session = useSession();
  const [getTeamReuse] = useLazyGetProposalTeamReuseQuery();
  const [saveTemplate] = useSaveProposalTemplateMutation();
  const [teams, setTeams] = useState<ProposalTemplateTeamChoices>(DEFAULT_TEAM_CHOICES);

  const { action, dialog } = useCreateTemplateAction({
    dialogTitle: SAVE_AS_PROPOSAL_TEMPLATE_LABEL,
    defaultName: proposal?.name?.value ?? "",
    onOpen: () => setTeams(DEFAULT_TEAM_CHOICES),
    options: (
      <ProposalTemplateTeamChoicesFields
        party={proposalTemplatePartyOf(session)}
        choices={teams}
        onChange={setTeams}
      />
    ),
    onCreate: (name) =>
      saveTemplateFrom({
        name,
        teams,
        proposal,
        session,
        fetchTeamReuse: async (proposalId) =>
          ProposalTeamReuse.fromJSON(await getTeamReuse({ proposalId: proposalId.value }).unwrap()),
        save: (payload) => saveTemplate(payload).unwrap(),
      }),
  });

  const offered = Boolean(proposal?.id) && canCreateProposals && !proposal?.frozenAt;
  const isSaving = Boolean(action.loading);
  return {
    actions: offered
      ? [
          {
            key: "save-as-proposal-template",
            label: SAVE_AS_PROPOSAL_TEMPLATE_LABEL,
            icon: action.icon,
            tooltip: SAVE_AS_PROPOSAL_TEMPLATE_TOOLTIP,
            loading: isSaving,
            disabled: action.disabled,
            onClick: () => action.onClick(),
          },
        ]
      : [],
    dialog,
    open: action.onClick,
    isSaving,
  };
}

/**
 * Module scope, as `saveTeamTemplate` is, so the React Compiler sees no try/catch in the component.
 */
async function saveTemplateFrom(args: {
  name: string;
  teams: ProposalTemplateTeamChoices;
  proposal: Proposal | null | undefined;
  session: Session;
  fetchTeamReuse: (proposalId: Guid) => Promise<ProposalTeamReuse>;
  save: (payload: SaveProposalTemplatePayload) => Promise<unknown>;
}): Promise<boolean> {
  const { proposal, ...rest } = args;
  if (!proposal?.id) return false;
  return saveProposalAsTemplate({ ...rest, proposal });
}
